import './AppTable.scss';

import { AppNameFilter } from './AppNameFilter';
import { AppTableBulkEditModal } from './AppTableBulkEditModal';
import { BlockedApplicationAlert } from '@security/BlockedApplicationAlert';
import { ClassificationFilter } from '@security/audit-trail/filters';
import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getClosestCardBody } from '@lib/popups';
import { getColumns } from './AppTableColumns';
import { GroupFilter } from './GroupFilter';
import { HideAppNotifications } from './HideAppNotifications';
import { KeyPagination } from '@ui/KeyPagination';
import { paginationDefaultQueryParams } from '@periscopes/general';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { SelectionTable } from '@ui/tables';

import {
	ClassificationLevel,
	coreActions,
	DataSecurityApplication,
	DataSecurityApplicationStatus,
	SchemaFormat
} from '@sonar-software/sonar-core';
import { DynamicFilterEntry, SonarV3SortDirection, SortByOption } from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo, useState } from 'react';
import { TableFilter, TableModifier, TableQueryParams } from '@ui/table-modifier';
import {
	useAction,
	useApplicationReferences,
	useAsyncState,
	useOnMount,
	useQueryParams,
	useSelectionTableWithInversion
} from '@hooks';

export const appSelectors = createAsyncSelectors((state) => state.core.dataSecurity.applications.get);

export type AppTableDynamicFilters = {
	Name?: DynamicFilterEntry<Array<string>>;
	IntegrationUser?: DynamicFilterEntry<Array<string>>;
	StakeholderIds?: DynamicFilterEntry<Array<string>>;
	StakeholderGroupId?: DynamicFilterEntry<string>;
	OwnerId?: DynamicFilterEntry<string>;
	Classification?: DynamicFilterEntry<Array<ClassificationLevel>>;
};

export type AppTableQueryParams = TableQueryParams & AppTableDynamicFilters & {
	status: Array<DataSecurityApplicationStatus>;
};

const getRowKey = (app: DataSecurityApplication) => app.applicationId;

const filters: Array<TableFilter> = [
	{
		filterComponent: AppNameFilter,
		filterFormat: SchemaFormat.Apps,
		filterId: 'Name',
		filterLabel: COPY_SPECIFIC.APPLICATION
	},
	{
		filterFormat: SchemaFormat.SalesforceUser,
		filterId: 'IntegrationUser',
		filterLabel: COPY_SPECIFIC.USER
	},
	{
		filterFormat: SchemaFormat.Stakeholder,
		filterId: 'StakeholderIds',
		filterLabel: COPY_SPECIFIC.STAKEHOLDERS
	},
	{
		filterComponent: GroupFilter,
		filterFormat: SchemaFormat.Stakeholder,
		filterId: 'StakeholderGroupId',
		filterLabel: COPY_SPECIFIC.GROUPS
	},
	{
		filterFormat: SchemaFormat.Owner,
		filterId: 'OwnerId',
		filterLabel: COPY_SPECIFIC.OWNER
	},
	{
		filterComponent: ClassificationFilter,
		filterFormat: SchemaFormat.Object,
		filterId: 'Classification',
		filterLabel: COPY_SPECIFIC.CLASSIFICATION
	}
];

const sortOptions: Array<SortByOption> = [
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'Name',
		label: COPY_SPECIFIC.APPLICATION
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'IntegrationUser',
		label: COPY_SPECIFIC.USER
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'StakeholderGroupId',
		label: COPY_SPECIFIC.GROUP
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'OwnerId',
		label: COPY_SPECIFIC.OWNER
	},
	{
		direction: SonarV3SortDirection.Ascending,
		key: 'Classification',
		label: COPY_SPECIFIC.CLASSIFICATION
	}
];

const selectableStatus: Array<DataSecurityApplicationStatus> = [
	DataSecurityApplicationStatus.Added,
	DataSecurityApplicationStatus.Blocked
];

const convertToDql = (params: AppTableQueryParams) => queryParamsToDynamicQueryLanguageParams(params, {
	filterParams: {
		Classification: { value: (filter) => filter?.value },
		IntegrationUser: { value: (filter) => filter?.value },
		Name: { value: (filter) => filter?.value },
		OwnerId: { value: (filter) => filter?.value },
		StakeholderGroupId: { value: (filter) => filter?.value },
		StakeholderIds: { value: (filter) => filter?.value },
		status: true
	}
});

export const AppTable: React.FC = () => {
	const { data, isRequested } = useAsyncState(appSelectors);
	const [ popoverTarget, setPopoverTarget ] = useState<HTMLElement | null>(null);

	const allApps = useApplicationReferences();
	const loadApps = useAction(coreActions.requestGetCoreDataSecurityApplications);
	const hideApplication = useAction(coreActions.requestDeleteCoreDataSecurityApplications);

	const { updateParams, queryParams } = useQueryParams<AppTableQueryParams>({
		defaultParams: {
			...paginationDefaultQueryParams,
			sortBy: sortOptions[ 0 ].key,
			sortDirection: sortOptions[ 0 ].direction as SonarV3SortDirection,
			status: [ DataSecurityApplicationStatus.Added, DataSecurityApplicationStatus.Blocked ]
		},
		onUpdate: (newParams, prev, combined) => {
			loadApps(
				convertToDql(combined)
			);
		}
	});

	const selectableApps = useMemo(() => Object.entries(allApps)
		.map(([ id, app ]) => ({
			...app,
			applicationId: Number(id)
		} as DataSecurityApplication))
		.filter((app) => selectableStatus.includes(app.status)),
	[ allApps ]);

	const selectionData = useSelectionTableWithInversion({
		getRowKey,
		results: data?.results,
		totalCount: selectableApps.length
	});

	const typeLabel = selectionData.selectedCount > 1 ? COPY_SPECIFIC.APPLICATIONS : COPY_SPECIFIC.APPLICATION;

	useOnMount(() => {
		loadApps(convertToDql(queryParams));
	});

	const handleEditModalOpen = useCallback((button: HTMLElement) => {
		if (popoverTarget !== button) {
			setPopoverTarget(button);
		}
	}, [ popoverTarget ]);

	const handleHide = useCallback(
		(app: DataSecurityApplication) => {
			hideApplication({ applicationId: Number(app.applicationId) });
		},
		[ hideApplication ]
	);

	const columns = getColumns(handleHide);

	const hasBlockedApps =
		Boolean(data?.results.filter((x) => x.status === DataSecurityApplicationStatus.Blocked).length);

	return (
		<>
			{ hasBlockedApps && <BlockedApplicationAlert /> }
			<AppTableBulkEditModal
				popoverTarget={ popoverTarget }
				selectableApps={ selectableApps }
				selectionData={ selectionData }
				typeLabel={ typeLabel } />
			<TableModifier
				convertToDynamicQueryLanguage={ (params) => {
					updateParams(params as AppTableQueryParams);

					return convertToDql(params as AppTableQueryParams);
				} }
				filterDisplayType='menu'
				filters={ filters }
				filtersString={ queryParams.filters }
				onChange={ updateParams }
				orderString={ queryParams.ordering }
				sortOptions={ sortOptions } />
			<SelectionTable
				className={ classNames({ isEmpty: !data || !data.results || !data.results.length }) }
				columns={ columns }
				dataSource={ data?.results }
				getPopupContainer={ getClosestCardBody }
				id='app-table'
				loading={ isRequested }
				onEditSelection={ handleEditModalOpen }
				rowKey={ getRowKey }
				scroll={ { x: 'max-content' } }
				selectionData={ selectionData }
				selectionType={ typeLabel } />
			<KeyPagination
				defaultPageSize={ queryParams.count }
				onChange={ updateParams }
				resource={ data } />
			<HideAppNotifications />
		</>);
};
